/**
 * Public /board page — Board of Directors + Organizing Committee roster
 * for the Proposed Trillion Dollar Spending Power Federal Credit Union.
 *
 * This page is purposely built to serve TWO audiences:
 *   1. NCUA Form 4001 field investigators who check the organizing group's
 *      web presence during Phase 1.
 *   2. Instagram traffic deciding whether to pledge $100 — showing real
 *      names + bios is one of the strongest conversion signals.
 *
 * Fetches `/api/board/public` which NEVER returns email / phone.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Board() {
  const [data, setData] = useState({ board: [], committee: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.title = "Board & Committee · Proposed TDSPFCU";
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/board/public`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData({ board: j.board || [], committee: j.committee || [] });
      } catch (e) {
        if (!cancelled) setErr(e.message || "Could not load the roster.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalPeople = data.board.length + data.committee.length;

  return (
    <div className="bg-bdu-bg text-bdu-cream min-h-screen" data-testid="board-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pt-20 pb-14">
        <div className="label-eyebrow flex items-center gap-2">
          <ShieldCheck size={12} className="text-bdu-gold" /> Leadership · Proposed TDSPFCU
        </div>
        <h1
          className="mt-6 font-serif text-5xl sm:text-6xl text-bdu-cream leading-[1.03]"
          data-testid="board-h1"
        >
          The people <span className="italic text-bdu-gold">organizing</span> the credit union.
        </h1>
        <p className="mt-6 text-lg text-bdu-cream/70 max-w-2xl leading-relaxed">
          Our Board of Directors and Organizing Committee are the people the
          NCUA will meet during Phase 1 of the Proposed Trillion Dollar
          Spending Power Federal Credit Union charter application. Every one
          of them lives inside or directly serves our Fulton + DeKalb Field of
          Membership.
        </p>
        <div className="mt-7 flex flex-wrap gap-4 text-xs tracking-[0.2em] uppercase text-bdu-cream/55">
          <span className="flex items-center gap-2"><Users size={12} className="text-bdu-gold" /> {totalPeople} charter organizers</span>
          <span className="flex items-center gap-2"><MapPin size={12} className="text-bdu-gold" /> Fulton · DeKalb</span>
        </div>
      </section>

      <div className="h-px max-w-6xl mx-auto bg-bdu-gold/15" />

      {/* Roster */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-16" data-testid="board-roster">
        {loading && (
          <div className="text-sm text-bdu-cream/50" data-testid="board-loading">Loading roster…</div>
        )}
        {err && !loading && (
          <div className="border border-amber-500/40 bg-amber-500/5 text-amber-200 text-sm p-4" data-testid="board-error">
            {err}
          </div>
        )}
        {!loading && !err && (
          <>
            <RosterGroup
              title="Board of Directors"
              eyebrow="Governance"
              people={data.board}
              empty="Our Board of Directors is being seated. Check back shortly."
              testId="board-group-board"
            />
            <div className="h-16" />
            <RosterGroup
              title="Organizing Committee"
              eyebrow="Operations"
              people={data.committee}
              empty="Our Organizing Committee is being seated. Check back shortly."
              testId="board-group-committee"
            />
          </>
        )}
      </section>

      {/* CTA */}
      <section className="bg-bdu-surface border-t border-bdu-gold/15">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center">
          <div className="label-eyebrow justify-center flex items-center gap-2">
            <ShieldCheck size={12} className="text-bdu-gold" /> Join the 3,000
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl text-bdu-cream leading-[1.1]">
            Great leaders need <span className="italic text-bdu-gold">great neighbors.</span>
          </h2>
          <p className="mt-6 text-bdu-cream/70 max-w-2xl mx-auto leading-relaxed">
            Reserve your charter-supporter spot with a non-binding $100 pledge
            held in escrow at Citizen Trust Bank — refunded in full if the
            NCUA does not grant our charter.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/credit-union" className="btn-gold inline-flex items-center gap-2" data-testid="board-cta-pledge">
              Pledge $100 at Citizen Trust <ArrowRight size={16} />
            </Link>
            <Link
              to="/field-of-membership"
              className="inline-flex items-center gap-2 border border-bdu-gold/40 text-bdu-cream hover:bg-bdu-gold/10 px-6 py-3 text-xs tracking-[0.2em] uppercase transition"
              data-testid="board-cta-fom"
            >
              Check Your ZIP · Fulton + DeKalb
            </Link>
          </div>
          <p className="mt-12 text-[11px] text-bdu-cream/40 italic max-w-3xl mx-auto leading-relaxed">
            Proposed Trillion Dollar Spending Power Federal Credit Union —
            pending NCUA charter approval. All pledges are non-binding expressions of interest
            held in a segregated escrow account at Citizen Trust Bank.
          </p>
        </div>
      </section>
    </div>
  );
}

function RosterGroup({ title, eyebrow, people, empty, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-eyebrow">{eyebrow}</div>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-bdu-cream leading-tight">{title}</h2>
      <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {people.length === 0 && (
          <p className="text-sm text-bdu-cream/50 italic col-span-full" data-testid={`${testId}-empty`}>
            {empty}
          </p>
        )}
        {people.map((p) => (
          <PersonCard key={p.id} person={p} testId={`${testId}-${p.id}`} />
        ))}
      </div>
    </div>
  );
}

function PersonCard({ person, testId }) {
  const initials = (person.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  const location = [person.city, person.county && `${person.county} County`]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      className="border border-bdu-gold/20 bg-bdu-surface2 p-6 flex flex-col"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 shrink-0 border border-bdu-gold/50 bg-bdu-bg overflow-hidden flex items-center justify-center">
          {person.photo_url ? (
            <img
              src={person.photo_url}
              alt={person.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="font-serif text-xl text-bdu-gold">{initials || "·"}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg text-bdu-cream leading-snug">{person.name}</div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-bdu-gold mt-1">
            {person.title}
          </div>
          {location && (
            <div className="text-xs text-bdu-cream/55 mt-1 flex items-center gap-1">
              <MapPin size={10} /> {location}
            </div>
          )}
        </div>
      </div>
      {person.bio && (
        <p className="mt-4 text-sm text-bdu-cream/70 leading-relaxed">{person.bio}</p>
      )}
      {person.linkedin_url && (
        <a
          href={person.linkedin_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-[10px] tracking-[0.22em] uppercase text-bdu-gold hover:text-bdu-cream transition inline-flex items-center gap-1"
          data-testid={`${testId}-linkedin`}
        >
          LinkedIn →
        </a>
      )}
    </div>
  );
}
